import React, { useEffect, useState } from "react";
import StoreHeader from "./StoreHeader";
import {
  fetchShopifyCollection,
  addToCart,
  createCart,
  removeCartLine,
  fetchCart,
} from "./ShopifyClient";
import { useToast } from "./ToastProvider";
import "../css/Store.css";
import LocationDetails from "./LocationDetails";

type Product = {
  id: string;
  title: string;
  images: { src: string; altText?: string | null }[];
  variants: {
    id: string;
    priceV2: { amount: string; currencyCode: string };
  }[];
};

type CartLine = {
  id: string;
  quantity: number;
  merchandise: {
    id: string;
    title: string;
    product: { title: string };
  };
};

function Vouchers() {
  const [products, setProducts] = useState<Product[]>([]);
  const [cartId, setCartId] = useState<string | null>(
    localStorage.getItem("cart_id") || null
  );
  const [cartLines, setCartLines] = useState<CartLine[]>([]);
  const { showToast } = useToast();

  useEffect(() => {
    async function loadProducts() {
      const data = await fetchShopifyCollection("gift-vouchers");
      setProducts(data);
    }
    loadProducts();
    loadCart();
  }, []);

  async function loadCart() {
    const currentCartId = localStorage.getItem("cart_id");
    if (currentCartId) {
      const cart = await fetchCart(currentCartId);
      if (cart?.lines) {
        setCartLines(cart.lines.edges.map((edge) => edge.node));
      }
    }
  }

  async function handleAddToCart(variantId: string) {
    try {
      let currentCartId = cartId;
      if (!currentCartId) {
        const cart = await createCart();
        if (cart) {
          currentCartId = cart.id;
          setCartId(currentCartId);
          localStorage.setItem("cart_id", currentCartId);
        }
      }
      if (!currentCartId) return;

      const updatedCart = await addToCart(currentCartId, variantId, 1);
      if (updatedCart) {
        window.dispatchEvent(new Event("cartUpdated"));
        showToast("Item added to cart successfully!", "success");
        loadCart(); // Refresh cart lines
      }
    } catch (err) {
      console.error("Error adding to cart:", err);
      showToast("Failed to add item to cart. Please try again.", "error");
    }
  }

  async function handleRemoveFromCart(variantId: string) {
    try {
      if (!cartId) return;

      // Find the cart line that matches this variant
      const cartLine = cartLines.find(
        (line) => line.merchandise.id === variantId
      );
      if (!cartLine) {
        showToast("Item not found in cart.", "error");
        return;
      }

      const updatedCart = await removeCartLine(cartId, cartLine.id);
      if (updatedCart) {
        window.dispatchEvent(new Event("cartUpdated"));
        showToast("Item removed from cart successfully!", "success");
        loadCart(); // Refresh cart lines
      }
    } catch (err) {
      console.error("Error removing from cart:", err);
      showToast("Failed to remove item from cart. Please try again.", "error");
    }
  }

  // Helper function to check if a variant is in the cart
  function isInCart(variantId: string): boolean {
    return cartLines.some((line) => line.merchandise.id === variantId);
  }

  return (
    <>
      <StoreHeader />
      <section className="hero text-center py-20 ">
  <h1 className="mt-3 text-4xl fw-bold" style={{ color: "#155d68" }}>
    Explore Our Vouchers
  </h1>
  <p className="lead text-lg mt-2" style={{ color: "#155d68" }}>
    Treat someone special to the perfect gift — from wine tastings to dining
    experiences, our vouchers make every occasion unforgettable.
  </p>
</section>


      <section className="container py-5">
        <div className="row g-4">
          {products.length > 0 ? (
            products.map((product) => (
              <div key={product.id} className="col-12 col-md-6 col-lg-4">
                <div className="card h-100 shadow-sm rounded">
                  {product.images[0] && (
                    <img
                      src={product.images[0].src}
                      className="card-img-top"
                      alt={product.images[0].altText || product.title}
                    />
                  )}
                  <div className="card-body d-flex flex-column">
                    <h5 className="card-title">{product.title}</h5>
                    {product.variants[0] && (
                      <p className="card-text">
                        €{product.variants[0].priceV2.amount}{" "}
                        {product.variants[0].priceV2.currencyCode}
                      </p>
                    )}
                    <div className="mt-auto">
                      <button
                        className="btn-green w-100"
                        onClick={() => handleAddToCart(product.variants[0].id)}
                      >
                        Add to Cart
                      </button>
                      {isInCart(product.variants[0].id) && (
                        <button
                          className="btn-green w-100 bg-red-500 hover:bg-red-600 border-red-500 hover:border-red-600 mt-2"
                          onClick={() =>
                            handleRemoveFromCart(product.variants[0].id)
                          }
                          title="Remove from cart"
                        >
                          Remove from Cart
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p>Loading products...</p>
          )}
        </div>
      </section>

      <LocationDetails />
    </>
  );
}

export default Vouchers;
